// src/components/modals/ViewWarehouseProductionModal.jsx
import PropTypes from 'prop-types'
import { X, Package, Calendar, Hash, User, FileText, Warehouse as WarehouseIcon, ClipboardList } from 'lucide-react'

const ViewWarehouseProductionModal = ({ isOpen, onClose, production }) => {
  if (!isOpen || !production) return null

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
      style={{ zIndex: 999999 }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Production Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Production Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <Hash className="h-4 w-4 mr-1" />
                Production Number
              </div>
              <div className="font-semibold text-gray-900">
                {production.productionNumber}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <Calendar className="h-4 w-4 mr-1" />
                Created Date
              </div>
              <div className="font-semibold text-gray-900">
                {formatDate(production.createdAt)}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <WarehouseIcon className="h-4 w-4 mr-1" />
                Warehouse
              </div>
              <div className="font-semibold text-gray-900">
                {production.warehouse?.name || 'N/A'}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {production.warehouse?.address}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <User className="h-4 w-4 mr-1" />
                Created By
              </div>
              <div className="font-semibold text-gray-900">
                {production.createdBy?.username || 'N/A'}
              </div>
              <div className="text-sm text-gray-600 capitalize">
                {production.createdBy?.role}
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Package className="h-5 w-5 mr-2" />
              Product Information
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Product Name</div>
                  <div className="font-semibold text-gray-900">
                    {production.targetProduct?.name || production.product?.name || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">SKU</div>
                  <div className="font-semibold text-gray-900">
                    {production.targetProduct?.sku || production.product?.sku || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Type</div>
                  <div className="font-semibold text-gray-900 capitalize">
                    {(production.targetProduct?.type || production.product?.type)?.replace('_', ' ') || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Cost</div>
                  <div className="font-semibold text-gray-900">
                    {formatCurrency(production.targetProduct?.cost || production.product?.cost)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Outlet Price</div>
                  <div className="font-semibold text-gray-900">
                    {formatCurrency(production.targetProduct?.outletPrice || production.product?.outletPrice)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Quantity Produced</div>
                  <div className="font-semibold text-green-600 text-lg">
                    {production.quantityProduced} units
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-blue-300">
                <div className="text-sm text-gray-600">Expiry Date</div>
                <div className="font-semibold text-red-600">
                  {formatDate(production.expiryDate)}
                </div>
              </div>
            </div>
          </div>

          {/* Recipe Details */}
          {production.recipe && (
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ClipboardList className="h-5 w-5 mr-2" />
                Recipe Information
              </h3>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-gray-600">Recipe Name</div>
                    <div className="font-semibold text-gray-900">
                      {production.recipe.name}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Recipe Cost</div>
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(production.recipe.cost)}
                    </div>
                  </div>
                </div>
                
                {/* Raw Materials in Recipe */}
                {production.recipe.rawMaterialList && production.recipe.rawMaterialList.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-purple-300">
                    <div className="text-sm font-medium text-gray-700 mb-2">Raw Materials Required (Per Unit)</div>
                    <div className="space-y-2">
                      {production.recipe.rawMaterialList.map((item, index) => (
                        <div key={index} className="flex justify-between items-center bg-white p-2 rounded">
                          <span className="text-sm text-gray-900">
                            {item.rawMaterial?.name || item.rawMaterial}
                          </span>
                          <span className="text-sm font-medium text-gray-700">
                            Qty: {item.quantity}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Production Items (Consumed Raw Materials) */}
          {production.productionItems && production.productionItems.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Package className="h-5 w-5 mr-2" />
                Consumed Raw Materials
              </h3>
              <div className="bg-green-50 border border-green-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-green-200">
                  <thead className="bg-green-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Raw Material
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Code
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">
                        Required
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">
                        Consumed
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-green-100">
                    {production.productionItems.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {item.rawMaterial?.name || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.rawMaterial?.code || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {item.requiredQuantity}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                          {item.consumedQuantity}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Note */}
          {production.note && (
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Note
              </h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {production.note}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

ViewWarehouseProductionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  production: PropTypes.object
}

export default ViewWarehouseProductionModal
